import React from 'react';
import { SectionWrapper } from './SectionWrapper';
import { Activity, AlertTriangle, RefreshCw, BellRing } from 'lucide-react';
export function Layer6Monitoring() {
  const alerts = [{
    signal: 'GMV drops >30% for 7 days vs 30d avg',
    level: 'Watch',
    action: 'Auto-notify bank; flag in dashboard',
    color: 'bg-blue-100 text-blue-800 border-blue-200'
  }, {
    signal: 'GMV drops >50% for 14 days',
    level: 'Amber',
    action: 'Merchant outreach; review holdback rate',
    color: 'bg-amber-100 text-amber-800 border-amber-200'
  }, {
    signal: 'Zero settlements for 5+ consecutive days',
    level: 'Red',
    action: 'Investigate — has processing stopped?',
    color: 'bg-red-100 text-red-800 border-red-200'
  }, {
    signal: 'Repayment velocity < 60% of projection',
    level: 'Red',
    action: 'Escalate to bank credit team immediately',
    color: 'bg-red-100 text-red-800 border-red-200'
  }, {
    signal: 'Chargeback rate spikes > 2%',
    level: 'Red',
    action: 'Freeze new top-up eligibility',
    color: 'bg-red-100 text-red-800 border-red-200'
  }, {
    signal: 'Holdback-generated repayment < floor for 2mo',
    level: 'Amber',
    action: 'Operational review; consider rate adjustment',
    color: 'bg-amber-100 text-amber-800 border-amber-200'
  }];
  return <SectionWrapper title="Layer 6: Live Monitoring & Early Warning" subtitle="Since repayment runs automatically through settlement deduction, monitoring is about identifying deteriorating merchants before they become a credit loss." data-id="element-421">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8" data-id="element-422">
        {/* Daily Signals */}
        <div className="lg:col-span-1 bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-id="element-423">
          <div className="flex items-center gap-2 mb-6" data-id="element-424">
            <Activity className="w-5 h-5 text-indigo-600" data-id="element-425" />
            <h3 className="font-semibold text-slate-900" data-id="element-426">
              Daily Computed Signals
            </h3>
          </div>
          <ul className="space-y-4" data-id="element-427">
            {['Actual vs projected cumulative repayment (velocity ratio)', '7-day rolling GMV vs 30-day baseline (momentum index)', 'Active trading day count vs prior period', 'Chargeback rate trend — 14-day rolling', 'Settlement gap detection — days without activity'].map((item, i) => <li key={i} className="flex items-start gap-3 text-sm text-slate-600" data-id="element-428">
                <BellRing className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" data-id="element-429" />
                <span data-id="element-430">{item}</span>
              </li>)}
          </ul>
        </div>

        {/* Alert Thresholds */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-id="element-431">
          <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center gap-2" data-id="element-432">
            <AlertTriangle className="w-5 h-5 text-amber-500" data-id="element-433" />
            <h3 className="font-semibold text-slate-900" data-id="element-434">
              Alert Thresholds & Escalation Path
            </h3>
          </div>
          <div className="overflow-x-auto" data-id="element-435">
            <table className="w-full text-sm text-left" data-id="element-436">
              <thead className="bg-slate-50/50 text-slate-500 font-medium border-b border-slate-100" data-id="element-437">
                <tr data-id="element-438">
                  <th className="px-4 py-3" data-id="element-439">Signal</th>
                  <th className="px-4 py-3" data-id="element-440">Level</th>
                  <th className="px-4 py-3" data-id="element-441">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-442">
                {alerts.map((alert, i) => <tr key={i} className="hover:bg-slate-50" data-id="element-443">
                    <td className="px-4 py-3 font-medium text-slate-800" data-id="element-444">
                      {alert.signal}
                    </td>
                    <td className="px-4 py-3" data-id="element-445">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-bold border ${alert.color}`} data-id="element-446">
                        {alert.level}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600" data-id="element-447">{alert.action}</td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Top-up Trigger */}
      <div className="bg-indigo-900 rounded-xl shadow-lg p-6 md:p-8 relative overflow-hidden text-indigo-100" data-id="element-448">
        <div className="absolute -right-10 -top-10 opacity-10" data-id="element-449">
          <RefreshCw className="w-64 h-64 text-indigo-300" data-id="element-450" />
        </div>
        <div className="relative z-10" data-id="element-451">
          <div className="flex items-center gap-3 mb-4" data-id="element-452">
            <div className="bg-indigo-800 p-2 rounded-lg" data-id="element-453">
              <RefreshCw className="w-6 h-6 text-indigo-300" data-id="element-454" />
            </div>
            <h3 className="text-xl font-bold text-white" data-id="element-455">
              Top-up Eligibility Trigger
            </h3>
          </div>
          <p className="leading-relaxed max-w-3xl mb-4 text-indigo-200" data-id="element-456">
            Once a merchant has repaid{' '}
            <strong className="text-white" data-id="element-457">60% or more</strong> of their
            outstanding loan balance, the engine automatically re-models a
            top-up offer using the latest 90-day data.
          </p>
          <p className="leading-relaxed max-w-3xl text-indigo-200" data-id="element-458">
            If the merchant still passes all eligibility gates and scores above
            the decline threshold, a new offer is generated. This creates a
            continuous lending relationship — a powerful retention and revenue
            mechanism for both the PSP and the bank.
          </p>
        </div>
      </div>
    </SectionWrapper>;
}